import type { ClinicInvoice } from "@/features/clinic/types/invoice";

import { DateTime } from "luxon";

import {
  isMockEnabled,
  MOCK_CONFIG,
  OUTCOME_FIXTURE_CONTACT_OFFSET,
} from "./config";
import { deriveAppointmentValue } from "./handlers/clinics/appointment-value";
import { seedMyInboxDashboard } from "./seed-dashboard";
import {
  generateAppointments,
  generateChannels,
  generateShowcaseChannels,
  getChannelByType,
  generateContacts,
  generateConversations,
  generateDepartments,
  generateDoctors,
  generateInvoices,
  generateAdvances,
  generateReceipts,
  generateLifecycles,
  generateOrders,
  generateOrganization,
  generateEcommerceOrganization,
  generateVariantAttributes,
  generateProducts,
  generateAdAccounts,
  generateAds,
  generateBroadcasts,
  generateClosingNotes,
  generateForms,
  generateSavedFiles,
  generateSegments,
  generateSnippets,
  generateTags,
  generateTeams,
  generateWebsites,
  generateWrapUps,
  generateTickets,
  generateTreatments,
  generateUsers,
  generateWorkflows,
  generateWorkspaces,
  updateTreatmentsWithDoctors,
  generateKnowledgeBaseEntries,
  generateKnowledgeSuggestions,
  generateCustomViews,
} from "./generators";
import {
  createId,
  createPastTimestamp,
  pickRandom,
  randomBool,
} from "./generators/base";
import { CLINICSOFT_MOCK_INTEGRATION } from "./handlers/integration";
import { seedMockEchoFlags } from "./handlers/reply-review";
import { applyMockAsRole } from "./handlers/role";
import { initializeSeed } from "./seed";
import { useMockStore } from "./store";

import {
  APPOINTMENT_BOOKED_BY,
  APPOINTMENT_OUTCOME,
  RESOLVE_BLOCKED_REASON,
  BOOKING_TYPE,
  type Appointment,
  type AppointmentBookedBy,
} from "@/features/clinic/types/appointment";
import {
  BILLING_SYNC_KIND,
  BILLING_SYNC_STATUS,
  type BillingSyncRun,
  type ClinicAdvance,
  type ClinicReceipt,
} from "@/features/clinic/types/billing";
import { ORG_ROLE } from "@/types/user";

export { isMockEnabled } from "./config";
export { useMockStore } from "./store";

// `?mockAs=agent` (or member, or a custom role's name like front-desk) signs
// in as an org member holding that role. It sticks for the tab so a reload
// after a redirect keeps it; `?mockAs=` goes back to the org admin.
const readMockAs = (): string | null => {
  if (typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).get("mockAs");

  try {
    if (fromUrl !== null) sessionStorage.setItem("replyn:mock-as", fromUrl);

    return fromUrl ?? sessionStorage.getItem("replyn:mock-as");
  } catch {
    return fromUrl;
  }
};

export function initializeMockData(): void {
  if (!isMockEnabled()) {
    return;
  }

  const store = useMockStore.getState();

  // Check if already initialized
  if (store.initialized) {
    return;
  }

  initializeSeed();

  const isFreshOnboarding =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("mock") ===
      "fresh-onboarding";
  const isEcommerceOrg =
    isFreshOnboarding || import.meta.env.VITE_MOCK_ORG_TYPE === "ecommerce";
  const organization = isEcommerceOrg
    ? generateEcommerceOrganization()
    : generateOrganization();

  const workspaces = generateWorkspaces(organization.id, 2);

  if (isFreshOnboarding) {
    workspaces[0].name = "Online Store";
    workspaces[1].name = "Customer Care";
  }

  const primaryWorkspace = workspaces[0];

  const users = generateUsers(organization.id, MOCK_CONFIG.userCount).map(
    (user, index) => ({
      ...user,
      workspaces:
        user.role === "organization:admin"
          ? []
          : workspaces.map((workspace) => ({
              id: workspace.id,
              name: workspace.name,
              role:
                index % 3 === 0
                  ? ("workspace:member" as const)
                  : ("workspace:agent" as const),
            })),
    }),
  );

  const teams = generateTeams(users);

  const channels = generateChannels(
    organization.id,
    primaryWorkspace.id,
    MOCK_CONFIG.channelCount,
  );

  const lifecycles = generateLifecycles(organization.id, primaryWorkspace.id);

  const tags = generateTags(
    organization.id,
    primaryWorkspace.id,
    MOCK_CONFIG.tagCount,
  );

  const treatments = generateTreatments(
    organization.id,
    primaryWorkspace.id,
    MOCK_CONFIG.treatmentCount,
  );

  const doctors = generateDoctors(
    organization.id,
    primaryWorkspace.id,
    treatments,
    MOCK_CONFIG.doctorCount,
  );

  updateTreatmentsWithDoctors(treatments, doctors);

  const departments = generateDepartments(
    organization.id,
    primaryWorkspace.id,
    doctors,
    treatments,
    users,
    MOCK_CONFIG.departmentCount,
  );

  const { contacts } = generateContacts({
    count: MOCK_CONFIG.contactCount,
    lifecycles,
    tags,
    users,
    channels,
    isEcommerce: isEcommerceOrg,
  });

  // Route a portion of contacts to a team so the team filter has data.
  if (teams.length > 0) {
    for (const contact of contacts) {
      if (randomBool(0.4)) {
        contact.assignedTeamId = pickRandom(teams).id;
      }
    }
  }

  const closingNotes = generateClosingNotes();

  const { conversations, messages, activities } = generateConversations({
    contacts,
    users,
    channels,
    doctors,
    treatments,
    closingNotes,
    organizationId: organization.id,
  });

  // Appointments (and the billing built on them) are a clinic-only concept -
  // e-commerce has orders instead - so none of this, including the
  // hand-authored "awaiting answer"/outcome fixtures, belongs on the
  // ecommerce mock persona.
  let appointments: Appointment[] = [];
  let invoices: ClinicInvoice[] = [];
  let advances: ClinicAdvance[] = [];
  let receipts: ClinicReceipt[] = [];

  if (!isEcommerceOrg) {
    appointments = generateAppointments({
      organizationId: organization.id,
      workspaceId: primaryWorkspace.id,
      doctors,
      treatments,
      contacts,
      userIds: users.map((u) => u.id),
    });
    // The generator only ever resolves a past day's appointments to
    // completed/cancelled/no_show, so without these the "unresolved" worklist
    // (past + still pending/confirmed) is permanently empty in mock mode.
    // Hand-authored rather than randomized so the count is guaranteed, not
    // merely likely, and spread across different days/doctors/statuses so the
    // list doesn't look like three copies of one row.
    const unresolvedBookedBySources: AppointmentBookedBy[] = [
      APPOINTMENT_BOOKED_BY.USER,
      APPOINTMENT_BOOKED_BY.CRM,
      APPOINTMENT_BOOKED_BY.AI,
    ];
    // The treatment the outcome fixtures are consultations *about* - the same
    // treatment the patient would go on to book, which is the pairing the funnel
    // measures. Without it the second worklist is empty in mock mode.
    const consultationTreatment = treatments[0] ?? null;

    // Both questions, and both booking types, so every path the banner can take
    // has something behind it: a service asks attendance and stops there, a
    // consultation asks attendance and then opens the outcome stepper, and a
    // completed consultation goes straight to the outcome question.
    const unresolvedFixtures: Appointment[] = [
      {
        daysAgo: 2,
        hour: 10,
        status: "pending" as const,
        doctorIndex: 0,
        bookingType: BOOKING_TYPE.CONSULTATION,
        isPaid: null,
      },
      {
        daysAgo: 4,
        hour: 14,
        status: "confirmed" as const,
        doctorIndex: 1,
        bookingType: BOOKING_TYPE.SERVICE,
        isPaid: null,
      },
      {
        daysAgo: 6,
        hour: 9,
        status: "pending" as const,
        doctorIndex: 2,
        bookingType: BOOKING_TYPE.SERVICE,
        isPaid: null,
      },
      {
        daysAgo: 9,
        hour: 15,
        status: "confirmed" as const,
        doctorIndex: 0,
        bookingType: BOOKING_TYPE.CONSULTATION,
        isPaid: null,
      },
      // Attended consultations nobody has said the outcome of - the second
      // question the banner asks, and the one the funnel report counts.
      {
        daysAgo: 1,
        hour: 11,
        status: "completed" as const,
        doctorIndex: 1,
        bookingType: BOOKING_TYPE.CONSULTATION,
        isPaid: null,
      },
      {
        daysAgo: 3,
        hour: 16,
        status: "completed" as const,
        doctorIndex: 2,
        bookingType: BOOKING_TYPE.CONSULTATION,
        isPaid: null,
      },
    ].map(({ daysAgo, hour, status, doctorIndex, bookingType }, index) => {
      const doctor = doctors[doctorIndex % doctors.length];
      const contact =
        contacts[(index + OUTCOME_FIXTURE_CONTACT_OFFSET) % contacts.length];
      const bookedBy =
        unresolvedBookedBySources[index % unresolvedBookedBySources.length];
      const startDate = DateTime.now()
        .minus({ days: daysAgo })
        .set({ hour, minute: 0, second: 0, millisecond: 0 });

      return {
        id: createId(),
        startDate: startDate.toISO() ?? "",
        endDate: startDate.plus({ minutes: 30 }).toISO() ?? "",
        doctorId: doctor.id,
        doctor: {
          id: doctor.id,
          name: doctor.name,
          photo: doctor.photo,
          externalId: doctor.externalId ?? null,
        },
        contactId: contact?.id ?? null,
        contact: contact
          ? {
              id: contact.id,
              firstName: contact.firstName,
              lastName: contact.lastName,
              phone: contact.phone,
              email: contact.email,
              imageUrl: contact.imageUrl,
              assignedUserId: contact.assignedUserId ?? null,
              origin: contact.origin ?? null,
            }
          : undefined,
        // The treatment a consultation was *about* - the one the patient would go
        // on to book, which is the pair the funnel measures.
        treatmentId:
          bookingType === BOOKING_TYPE.CONSULTATION
            ? (consultationTreatment?.id ?? null)
            : null,
        treatment:
          bookingType === BOOKING_TYPE.CONSULTATION && consultationTreatment
            ? { id: consultationTreatment.id, name: consultationTreatment.name }
            : null,
        patients: [],
        isPaid: null,
        status,
        // Mirrors the generator's convention: the mock viewer is always an
        // admin, so `null` (not `undefined`) is the "unpriced" contract.
        value: null,
        notes: null,
        bookingType,
        outcome: null,
        isResolveBlocked: false,
        resolveBlockedReason: null,
        originAppointmentId: null,
        color: status === "pending" ? "#3b82f6" : "#22c55e",
        organizationId: organization.id,
        workspaceId: primaryWorkspace.id,
        createdAt: createPastTimestamp(daysAgo + 7),
        updatedAt: createPastTimestamp(daysAgo),
        bookedBy,
        bookedByUserId:
          bookedBy === APPOINTMENT_BOOKED_BY.USER
            ? (users[0]?.id ?? null)
            : null,
      };
    });

    appointments.push(...unresolvedFixtures);

    // One consultation blocked waiting on the patient, with the asked-for date
    // already passed - the state the "did they decide?" banner and the outcome
    // queue exist for. Stale and payment blocks derive from the rows above.
    const waitingConsultationStart = DateTime.now()
      .minus({ days: 10 })
      .set({ hour: 11, minute: 0, second: 0, millisecond: 0 });
    const waitingContact = contacts[2 % contacts.length];
    const waitingDoctor = doctors[1 % doctors.length];

    appointments.push({
      id: createId(),
      startDate: waitingConsultationStart.toISO() ?? "",
      endDate: waitingConsultationStart.plus({ minutes: 30 }).toISO() ?? "",
      doctorId: waitingDoctor.id,
      doctor: {
        id: waitingDoctor.id,
        name: waitingDoctor.name,
        photo: waitingDoctor.photo,
        externalId: waitingDoctor.externalId ?? null,
      },
      contactId: waitingContact?.id ?? null,
      contact: waitingContact
        ? {
            id: waitingContact.id,
            firstName: waitingContact.firstName,
            lastName: waitingContact.lastName,
            phone: waitingContact.phone,
            email: waitingContact.email,
            imageUrl: waitingContact.imageUrl,
            assignedUserId: waitingContact.assignedUserId ?? null,
            origin: waitingContact.origin ?? null,
          }
        : undefined,
      treatmentId: null,
      treatment: null,
      patients: [],
      status: "completed" as const,
      value: null,
      notes: null,
      bookingType: BOOKING_TYPE.CONSULTATION,
      isPaid: null,
      outcome: null,
      isResolveBlocked: true,
      resolveBlockedReason: RESOLVE_BLOCKED_REASON.WAITING_ON_USER,
      waitingOnUserTill: DateTime.now().minus({ days: 2 }).toISO() ?? "",
      originAppointmentId: null,
      color: "#22c55e",
      organizationId: organization.id,
      workspaceId: primaryWorkspace.id,
      createdAt: createPastTimestamp(17),
      updatedAt: createPastTimestamp(10),
      bookedBy: APPOINTMENT_BOOKED_BY.USER,
      bookedByUserId: users[0]?.id ?? null,
    });

    // Three CRM states the front desk meets: a push ClinicSoft rejected, a
    // linked visit that drifted after a reschedule here, and a booking that only
    // exists here and can be pushed. The doctor gets a CRM id so the push is
    // offered rather than blocked.
    const crmDoctor = doctors[2 % doctors.length];

    crmDoctor.externalId = "4471";
    const crmDoctorSummary = {
      id: crmDoctor.id,
      name: crmDoctor.name,
      photo: crmDoctor.photo,
      externalId: crmDoctor.externalId,
    };
    const crmFixtureDay = DateTime.now().plus({ days: 1 });
    const crmFixture = (
      contact: (typeof contacts)[number],
      hour: number,
      minute: number,
    ) => {
      const start = crmFixtureDay.set({
        hour,
        minute,
        second: 0,
        millisecond: 0,
      });

      return {
        id: createId(),
        startDate: start.toISO() ?? "",
        endDate: start.plus({ minutes: 30 }).toISO() ?? "",
        doctorId: crmDoctor.id,
        doctor: crmDoctorSummary,
        contactId: contact.id,
        contact: {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          phone: contact.phone,
          email: contact.email,
          imageUrl: contact.imageUrl,
          assignedUserId: contact.assignedUserId ?? null,
          origin: contact.origin ?? null,
        },
        treatmentId: null,
        treatment: null,
        patients: [
          {
            firstName: contact.firstName ?? "",
            lastName: contact.lastName ?? "",
            phone: contact.phone ?? "",
            contactId: contact.id,
          },
        ],
        status: "confirmed" as const,
        value: null,
        notes: null,
        bookingType: BOOKING_TYPE.SERVICE,
        isPaid: true,
        outcome: null,
        isResolveBlocked: false,
        resolveBlockedReason: null,
        originAppointmentId: null,
        color: "#10b981",
        organizationId: organization.id,
        workspaceId: primaryWorkspace.id,
        createdAt: createPastTimestamp(3),
        updatedAt: createPastTimestamp(1),
        bookedBy: APPOINTMENT_BOOKED_BY.USER,
        bookedByUserId: users[0]?.id ?? null,
      };
    };

    appointments.push(
      {
        ...crmFixture(contacts[3 % contacts.length], 10, 0),
        externalId: null,
        externalData: {
          integrationId: "mock-integration-clinicsoft",
          error: {
            code: "SLOT_TAKEN",
            action: "create",
            message: "ClinicSoft rejected the visit",
            details: "Dr Sita Karki has no free slot at 10:00 that day.",
            occurredAt: DateTime.now().minus({ hours: 2 }).toISO() ?? "",
          },
        },
      },
      {
        ...crmFixture(contacts[4 % contacts.length], 11, 0),
        externalId: null,
      },
      {
        ...crmFixture(contacts[5 % contacts.length], 15, 30),
        externalId: "48197",
        externalData: {
          appointmentId: "48197",
          patientId: "1188",
          enquiryId: "9921",
          integrationId: "mock-integration-clinicsoft",
          isDrifted: true,
          drift: {
            message:
              "The time was changed here after the visit was created there.",
            occurredAt: DateTime.now().minus({ days: 1 }).toISO() ?? "",
            orphanedVisitId: "48197",
          },
        },
      },
    );

    // One consultation that already sold something, so both ends of the link have
    // something to render without having to walk the outcome flow first. The
    // service carries the value; the consultation carries what was quoted.
    const settledConsultationStart = DateTime.now()
      .minus({ days: 12 })
      .set({ hour: 10, minute: 0, second: 0, millisecond: 0 });
    const soldServiceStart = DateTime.now()
      .plus({ days: 4 })
      .set({ hour: 14, minute: 0, second: 0, millisecond: 0 });
    const settledConsultationId = createId();
    const linkedContact = contacts[0];
    const linkedDoctor = doctors[0];
    const linkedTreatment = consultationTreatment
      ? { id: consultationTreatment.id, name: consultationTreatment.name }
      : null;
    const linkedDoctorSummary = {
      id: linkedDoctor.id,
      name: linkedDoctor.name,
      photo: linkedDoctor.photo,
      externalId: linkedDoctor.externalId ?? null,
    };
    const linkedContactSummary = linkedContact
      ? {
          id: linkedContact.id,
          firstName: linkedContact.firstName,
          lastName: linkedContact.lastName,
          phone: linkedContact.phone,
          email: linkedContact.email,
          imageUrl: linkedContact.imageUrl,
          assignedUserId: linkedContact.assignedUserId ?? null,
          origin: linkedContact.origin ?? null,
        }
      : undefined;

    appointments.push(
      {
        id: settledConsultationId,
        startDate: settledConsultationStart.toISO() ?? "",
        endDate: settledConsultationStart.plus({ minutes: 30 }).toISO() ?? "",
        doctorId: linkedDoctor.id,
        doctor: linkedDoctorSummary,
        contactId: linkedContact?.id ?? null,
        contact: linkedContactSummary,
        treatmentId: linkedTreatment?.id ?? null,
        treatment: linkedTreatment,
        patients: [],
        status: "completed" as const,
        value: null,
        notes: null,
        bookingType: BOOKING_TYPE.CONSULTATION,
        isPaid: null,
        outcome: APPOINTMENT_OUTCOME.BOOKED_SERVICE,
        isResolveBlocked: false,
        resolveBlockedReason: null,
        originAppointmentId: null,
        color: "#22c55e",
        organizationId: organization.id,
        workspaceId: primaryWorkspace.id,
        createdAt: createPastTimestamp(19),
        updatedAt: createPastTimestamp(12),
        bookedBy: APPOINTMENT_BOOKED_BY.USER,
        bookedByUserId: users[0]?.id ?? null,
      },
      {
        id: createId(),
        startDate: soldServiceStart.toISO() ?? "",
        endDate: soldServiceStart.plus({ minutes: 90 }).toISO() ?? "",
        doctorId: linkedDoctor.id,
        doctor: linkedDoctorSummary,
        contactId: linkedContact?.id ?? null,
        contact: linkedContactSummary,
        treatmentId: linkedTreatment?.id ?? null,
        treatment: linkedTreatment,
        patients: [],
        status: "confirmed" as const,
        value: "8000.00",
        notes: null,
        bookingType: BOOKING_TYPE.SERVICE,
        isPaid: null,
        outcome: null,
        isResolveBlocked: false,
        resolveBlockedReason: null,
        originAppointmentId: settledConsultationId,
        color: "#3b82f6",
        organizationId: organization.id,
        workspaceId: primaryWorkspace.id,
        createdAt: createPastTimestamp(12),
        updatedAt: createPastTimestamp(12),
        bookedBy: APPOINTMENT_BOOKED_BY.USER,
        bookedByUserId: users[0]?.id ?? null,
      },
    );

    invoices = generateInvoices(appointments, contacts);
    advances = generateAdvances(contacts, appointments);
    receipts = generateReceipts(contacts, appointments, advances);
  }

  const variantAttributes = generateVariantAttributes(
    organization.id,
    primaryWorkspace.id,
  );
  const products = generateProducts(
    organization.id,
    primaryWorkspace.id,
    variantAttributes,
    MOCK_CONFIG.productCount,
  );

  const orders = generateOrders({
    contacts,
    channels,
    products,
    organizationId: organization.id,
    workspaceId: primaryWorkspace.id,
    count: MOCK_CONFIG.orderCount,
  });

  // Generate tickets
  const tickets = generateTickets(
    organization.id,
    primaryWorkspace.id,
    users,
    contacts,
    MOCK_CONFIG.ticketCount,
  );

  const snippets = generateSnippets(MOCK_CONFIG.snippetCount);

  const forms = generateForms();
  const websites = generateWebsites(organization.id, primaryWorkspace.id);
  const segments = generateSegments(tags, lifecycles, !isEcommerceOrg);
  const savedFiles = generateSavedFiles();
  const wrapUps = generateWrapUps(
    closingNotes.map((note) => note.id),
    lifecycles,
  );

  const adAccounts = generateAdAccounts(organization.id, primaryWorkspace.id);
  const ads = generateAds(
    organization.id,
    primaryWorkspace.id,
    adAccounts[0]?.id ?? null,
  );
  const broadcasts = generateBroadcasts({
    organizationId: organization.id,
    workspaceId: primaryWorkspace.id,
    channelId: getChannelByType(channels, "whatsapp")?.id ?? "",
    segmentIds: segments.map(({ id }) => id),
    isStore: isEcommerceOrg,
  });

  const workflows = generateWorkflows(organization.id, primaryWorkspace.id);

  // Clinic-flavored content (treatments, doctors, membership perks) - not the
  // ecommerce mock persona's business.
  const knowledgeBaseEntries = isEcommerceOrg
    ? []
    : generateKnowledgeBaseEntries(organization.id, primaryWorkspace.id);
  const knowledgeSuggestions = isEcommerceOrg
    ? []
    : generateKnowledgeSuggestions(contacts.map((contact) => contact.id));

  const customViews = generateCustomViews(users[0].id, tags, lifecycles);

  store.setOrganization(organization);
  store.setWorkspaces(workspaces);
  store.setUsers(isFreshOnboarding ? users.slice(0, 1) : users);
  const mockAs = readMockAs();
  const mockAsUser = mockAs
    ? users.find((user) => user.role === ORG_ROLE.MEMBER)
    : undefined;

  store.setCurrentUserId((mockAsUser ?? users[0]).id);
  if (mockAs && mockAsUser) applyMockAsRole(mockAs, mockAsUser.id);
  store.setChannels(
    isFreshOnboarding
      ? []
      : [...channels, ...generateShowcaseChannels(organization.id)],
  );
  store.setLifecycles(lifecycles);
  store.setTags(tags);
  store.setDoctors(doctors);
  store.setTreatments(treatments);
  store.setDepartments(departments);

  seedMyInboxDashboard(
    conversations,
    appointments,
    users[0],
    users[1] ?? users[0],
    contacts,
    activities,
  );

  store.setAppointments(appointments);
  store.setInvoices(invoices);
  store.setReceipts(receipts);
  store.setAdvances(advances);
  // The generators price visits from their invoices alone; the seeded
  // receipts and advances outrank those the way the server's ledger does.
  new Set(
    [...receipts, ...advances]
      .map((row) => row.appointmentId)
      .filter((id): id is string => id !== null),
  ).forEach(deriveAppointmentValue);
  store.setBillingSyncRuns(seedBillingSyncRuns(users[0]?.id ?? null));
  store.setContacts(isFreshOnboarding ? [] : contacts);
  store.setConversations(conversations);
  store.setMessages(messages);
  store.setActivities(activities);
  store.setVariantAttributes(variantAttributes);
  store.setProducts(isFreshOnboarding ? [] : products);
  store.setOrders(isFreshOnboarding ? [] : orders);
  store.setTickets(tickets);
  store.setSnippets(snippets);
  store.setTeams(teams);
  store.setForms(forms);
  store.setWebsites(websites);
  store.setSegments(segments);
  store.setClosingNotes(isFreshOnboarding ? [] : closingNotes);
  store.setSavedFiles(savedFiles);
  store.setWrapUps(wrapUps);
  store.setAdAccounts(adAccounts);
  store.setAds(ads);
  store.setBroadcasts(broadcasts);
  store.setWorkflows(workflows);
  store.setKnowledgeBaseEntries(knowledgeBaseEntries);
  store.setKnowledgeSuggestions(knowledgeSuggestions);
  store.setCustomViews(customViews);
  seedMockEchoFlags();
  store.setInitialized(true);

  console.log("[Mock] Mock data initialized:", {
    organization: organization.name,
    workspaces: workspaces.length,
    users: users.length,
    channels: channels.length,
    contacts: contacts.length,
    doctors: doctors.length,
    treatments: treatments.length,
    departments: departments.length,
    appointments: appointments.length,
    products: products.length,
    orders: orders.length,
    tickets: tickets.length,
    snippets: snippets.length,
    teams: teams.length,
  });
}

/**
 * Two finished runs so the ClinicSoft drawer opens with a history rather than
 * an empty panel: one clean pull and one the CRM rejected.
 */
function seedBillingSyncRuns(
  requestedByUserId: string | null,
): BillingSyncRun[] {
  const base = {
    integrationId: CLINICSOFT_MOCK_INTEGRATION.id,
    kind: BILLING_SYNC_KIND.DATE_RANGE,
    requestedByUserId,
    mrn: null,
  };

  return [
    {
      ...base,
      id: "mock-billing-sync-run-1",
      status: BILLING_SYNC_STATUS.SUCCEEDED,
      fromDate: "2026-07-01",
      toDate: "2026-07-31",
      counts: {
        invoices: 42,
        receipts: 37,
        advances: 9,
        contactsCreated: 4,
        skipped: 2,
      },
      error: null,
      startedAt: createPastTimestamp(14),
      finishedAt: createPastTimestamp(14),
      createdAt: createPastTimestamp(14),
    },
    {
      ...base,
      id: "mock-billing-sync-run-2",
      status: BILLING_SYNC_STATUS.FAILED,
      fromDate: "2026-08-01",
      toDate: "2026-08-15",
      counts: null,
      error: "ClinicSoft returned 502 while listing receipts",
      startedAt: createPastTimestamp(3),
      finishedAt: createPastTimestamp(3),
      createdAt: createPastTimestamp(3),
    },
  ];
}

export function resetMockData(): void {
  const store = useMockStore.getState();

  store.reset();
  initializeMockData();
}
